import { memo, useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '@/api/client'
import { fmtDate, fmtEur, fmtNumber, translator, useT } from '@/i18n'
import { aufmassMessages } from '@/i18n/messages/aufmass'
import {
  EDITABLE_FIELDS, FLAG_REASON, PRESENCE,
  type AufmassPage as PageData, type AufmassRow, type PositionColumn,
} from './types'

/**
 * The workbook as a spreadsheet you can type into.
 *
 * WHAT AN EDIT DOES, because it is not what "editable grid" usually implies: nothing here
 * writes to AU. Corrections land in `aufmass.session_override` and are layered back over the
 * AU-derived row on read. That is not caution for its own sake — the refresher rewrites every
 * derived column of `fact_session` whenever a crew-day's source hash changes, so an edit
 * stored in the read model would be gone inside half an hour. Overrides survive precisely
 * because the refresher does not know they exist.
 *
 * Only descriptive columns are editable, and the greyed ones are greyed for a reason: no
 * CELL edit feeds a total, so a correction can never make the stat cards disagree with the
 * grid. Quantities and money are read-only — they are price-resolved server-side, and a
 * corrected metre count beside a fee derived from the uncorrected one is worse than no edit.
 *
 * The one number that does move is Total Metri, and it moves through a different door: RTK
 * geometry edits on the map (aufmass.geo_segment_override) adjust it IN SQL, on both the row
 * and the whole-set totals, so tiles and grid still cannot drift. The cell itself stays
 * uneditable here — it only carries the override provenance (tint + tooltip) via
 * `overrideField`, without ever becoming a typing target.
 */

interface Column {
  key: string
  label: string
  editable: boolean
  /** The API field name; set only on editable columns. */
  field?: string
  /**
   * Override-provenance key for a READ-ONLY column whose value the server adjusts (Total
   * Metri via RTK geometry edits). Drives the tint/tooltip lookup only — never editing,
   * which is gated on `editable && field`.
   */
  overrideField?: string
  get: (row: AufmassRow) => string
  align?: 'right'
  width?: string
  /** 1-based Excel column index (A=1 … BX=76), drives the header colour to match the workbook. */
  excel?: number
  /** Server sort key. Set → the header is clickable and toggles the sort. */
  sortKey?: string
  /**
   * Raw value to seed the editor with, when the display getter transforms it (e.g. ROM shows a
   * "not set" placeholder). Defaults to `get`. Editing and commit compare against THIS, so the
   * placeholder text can never be saved as an override.
   */
  editValue?: (row: AufmassRow) => string
}

/**
 * Header fill colour by Excel column index, read straight from the "2026 Aufmassi" workbook so the
 * grid's column groups are the colours people already know: descriptive green, Trasse peach,
 * Komplet/Prepucana blue, cable orange, Synergy blue, Ispod gold, Total Metri yellow, money amber.
 */
function excelHeaderColor(i?: number): string | undefined {
  if (!i) return undefined
  if (i <= 18) return '#A9D18E'        // A–R  descriptive
  if (i <= 25) return '#F8CBAD'        // S–Y  Trasse surface
  if (i <= 28) return '#BFBFBF'        // Z–AB Oberfläche
  if (i <= 32) return '#9DC3E6'        // AC–AF Komplet
  if (i === 33) return '#E7E6E6'       // AG  Š
  if (i <= 37) return '#F4B183'        // AH–AK RV07
  if (i <= 39) return '#8FAADC'        // AL–AM Synergy
  if (i <= 42) return '#FFD966'        // AN–AP Ispod
  if (i <= 45) return '#A6A6A6'        // AQ–AS Dubina
  if (i <= 47) return '#9DC3E6'        // AT–AU Prepucana
  if (i <= 49) return '#A9D18E'        // AV–AW HA
  if (i <= 53) return '#F4B183'        // AX–BA Arbeiten
  if (i <= 57) return '#DBDBDB'        // BB–BE
  if (i === 58) return '#FFFF00'       // BF  Total Metri
  if (i === 59) return '#BFBFBF'       // BG  Priprema
  return '#FFC000'                     // BH–BX money / classification
}

// Cell numbers and dates follow the chosen language (1.234,5 / 1,234.5), like the rest of the
// app. Read at render time, so a language switch re-formats the whole sheet.
const nf = { format: (n: number) => fmtNumber(n, 2) }
const eur = { format: (n: number) => fmtEur(n) }
const dash = (v: string | null | undefined) => v ?? ''

/** Cap on how wide any one column gets. Longer content truncates with an ellipsis + a title
 *  tooltip, so a free-text cell like "Izvjestaj" can't blow the column out to the whole screen. */
const CELL_MAX_W = 240
/** Height of the Excel-style formula bar that sits above the grid header. */
const FORMULA_BAR_H = 34

/** Keep the workbook's line breaks so headers stack vertically — a numeric column then only
 *  needs to be as wide as its widest word, not its full label. Trims blank / edge lines. */
const stackLabel = (s: string) => s.split('\n').map((l) => l.trim()).filter(Boolean).join('\n')
/** One-line version for tooltips / aria. */
const tidy = (s: string) => s.replace(/\s+/g, ' ').trim()
/** A short one-line name: drop the "(1)" code prefix and the trailing "(m)" unit. */
const shortLabel = (s: string) => {
  const t = tidy(s)
  const out = t.replace(/^\([^)]*\)\s*/, '').replace(/\s*\([^)]*\)\s*$/, '').trim()
  return out || t
}

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000'
const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

// ROM is an employees FK. Once refreshed it holds a name, but until then (or when AU never
// assigned one) it can still arrive as a GUID. Never show a GUID: the all-zeros / any raw GUID
// reads as "not set", everything else is a real name.
const romText = (v: string | null | undefined) => {
  const s = (v ?? '').trim()
  // Translated at render time (cells call this while rendering), never at module load.
  if (!s || s === EMPTY_GUID || GUID_RE.test(s)) return translator(aufmassMessages)('romNotSet')
  return s
}
// The value to EDIT: a GUID / unset ROM is not a name, so editing starts from an empty box.
const romRaw = (v: string | null | undefined) => {
  const s = (v ?? '').trim()
  return !s || s === EMPTY_GUID || GUID_RE.test(s) ? '' : s
}

function buildColumns(positions: PositionColumn[], showPositions: boolean): Column[] {
  const ed = (key: string, get: (r: AufmassRow) => string, excel: number, sortKey?: string): Column => ({
    key, field: key, label: EDITABLE_FIELDS[key]!, editable: true, get, excel,
    ...(sortKey ? { sortKey } : {}),
  })

  const cols: Column[] = [
    { key: 'workDate', label: 'Datum', editable: false, width: '6.5rem', excel: 1, sortKey: 'work_date',
      get: (r) => fmtDate(r.workDate) },
    { key: 'isoWeek', label: 'KW', editable: false, align: 'right', width: '3rem', excel: 73, sortKey: 'iso_week',
      get: (r) => String(r.isoWeek) },
    { key: 'projectName', label: 'Projektname', editable: false, excel: 67, sortKey: 'project', get: (r) => dash(r.projectName) },
    { key: 'companyName', label: 'Firma', editable: false, excel: 5, sortKey: 'company', get: (r) => dash(r.companyName) },
    { key: 'firmaType', label: 'MIH / SUB', editable: false, width: '5rem', excel: 70, sortKey: 'firma_type',
      get: (r) => dash(r.firmaType) },

    ed('kolona_leader_name', (r) => dash(r.kolonaLeaderName), 3, 'crew'),
    ed('bauleiter_name', (r) => dash(r.bauleiterName), 2, 'bauleiter'),
    ed('city', (r) => dash(r.city), 13, 'city'),
    ed('provider_name', (r) => dash(r.providerName), 14, 'provider'),
    ed('streets', (r) => dash(r.streets), 15, 'streets'),
    ed('pops', (r) => dash(r.pops), 16, 'pops'),
    ed('dps', (r) => dash(r.dps), 17, 'dps'),
    { ...ed('rom', (r) => romText(r.rom), 69, 'rom'), editValue: (r) => romRaw(r.rom) },
    ed('comment', (r) => dash(r.comment), 18, 'comment'),

    { key: 'workerCount', label: 'Broj radnika', editable: false, align: 'right', excel: 6, sortKey: 'worker_count',
      get: (r) => nf.format(r.workerCount) },
    { key: 'totalManHours', label: 'Radni sati', editable: false, align: 'right', excel: 12, sortKey: 'hours',
      get: (r) => (r.totalManHours ? nf.format(r.totalManHours) : '') },
  ]

  if (showPositions) {
    for (const p of positions.slice().sort((a, b) => a.exportColumn - b.exportColumn)) {
      cols.push({
        key: `pos:${p.exportColumn}`,
        label: p.label,   // raw, with the workbook's line breaks — the header stacks them
        editable: false,
        align: 'right',
        excel: p.exportColumn, // export column == the workbook's Excel column index
        get: (r) => {
          const q = r.positions[String(p.exportColumn)]
          return q ? nf.format(q) : ''
        },
      })
    }
  }

  cols.push(
    { key: 'totalMetri', label: 'Total Metri', editable: false, overrideField: 'total_metri',
      align: 'right', excel: 58, sortKey: 'metri',
      get: (r) => (r.totalMetri ? nf.format(r.totalMetri) : '') },
    { key: 'moneySub', label: 'Ucinak SUB', editable: false, align: 'right', excel: 63, sortKey: 'money_sub',
      get: (r) => (r.moneySub ? eur.format(r.moneySub) : '') },
    { key: 'moneyMih', label: 'Ucinak MIH', editable: false, align: 'right', excel: 64, sortKey: 'money_mih',
      get: (r) => (r.moneyMih ? eur.format(r.moneyMih) : '') },
    { key: 'pricelistSubName', label: 'Cjenovnik', editable: false, excel: 62, sortKey: 'pricelist_sub',
      get: (r) => dash(r.pricelistSubName) },
  )

  return cols
}

interface GridRowProps {
  row: AufmassRow
  ri: number
  columns: Column[]
  rowOffset: number
  /** Column index of the cursor within THIS row, or -1 when the cursor is elsewhere. */
  activeCol: number
  /** The in-cell draft, but only when the cursor is in this row (null otherwise). */
  draft: string | null
  onSelect: (r: number, c: number) => void
  onEdit: (r: number, c: number) => void
  setDraft: (v: string | null) => void
  commit: (v: string | null) => void
  inputRef: RefObject<HTMLInputElement | null>
}

/**
 * One spreadsheet row, memoized. A click only changes the cursor, so only the row that gains and
 * the row that loses the cursor re-render — the other ~1000 rows are skipped. Every prop here is
 * primitive or a stable reference (see the refs in EditableGrid), which is what makes memo hold.
 */
const GridRow = memo(function GridRow({
  row, ri, columns, rowOffset, activeCol, draft, onSelect, onEdit, setDraft, commit, inputRef,
}: GridRowProps) {
  const t = useT(aufmassMessages)
  return (
    <tr className={[
      row.flagReason ? 'row-flagged' : null,
      row.rtkMetriDelta ? 'row-rtk-changed' : null,
    ].filter(Boolean).join(' ') || undefined}>
      <td className="sticky left-0 z-20 w-12 min-w-12 border-b border-r border-neutral-200 bg-neutral-100 px-2 py-1.5 text-center text-xs tabular text-neutral-400">
        {rowOffset + ri + 1}
      </td>
      {columns.map((col, ci) => {
        const active = ci === activeCol
        const ovKey = col.overrideField ?? col.field
        const ov = ovKey ? row.overrides?.[ovKey] : undefined
        const editing = active && draft !== null
        const text = col.get(row)
        return (
          <td
            key={col.key}
            data-cell={`${ri}:${ci}`}
            role="gridcell"
            aria-selected={active}
            onClick={() => onSelect(ri, ci)}
            onDoubleClick={() => onEdit(ri, ci)}
            title={ov
              ? t('correctedBy', { name: ov.editedBy, date: fmtDate(ov.editedAt), original: ov.original ?? t('empty') })
              : undefined}
            className={`whitespace-nowrap border-b border-r border-neutral-200 bg-white px-3 py-1.5 ${
              ci === 0 ? 'sticky left-12 z-10' : ''
            } ${col.align === 'right' ? 'tabular text-right' : ''} ${
              col.editable ? 'cursor-cell' : 'text-neutral-500'
            } ${active ? 'ring-2 ring-inset ring-brand-600' : ''}`}
            style={{
              maxWidth: CELL_MAX_W,
              ...(ov ? { backgroundColor: 'color-mix(in srgb, var(--viz-series-1) 12%, var(--cell-bg, transparent))' } : {}),
            }}
          >
            {editing ? (
              <input
                ref={inputRef}
                value={draft ?? ''}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={() => { commit(draft); setDraft(null) }}
                aria-label={`${col.label}, ${fmtDate(row.workDate)}`}
                className="w-full min-w-24 bg-transparent outline-none"
              />
            ) : (
              <div className={`flex items-center gap-1 ${col.align === 'right' ? 'justify-end' : ''}`}>
                {/* Truncated to the column cap; hover shows the whole value. */}
                <span className="min-w-0 truncate" title={text || undefined}>{text}</span>
                {ov && (
                  <span aria-hidden className="shrink-0 text-[9px]" style={{ color: 'var(--viz-series-1)' }}>●</span>
                )}
                {/* The flag has to survive on the row you can edit, or a corrected crew-day
                    silently loses the mark saying it needs a human. */}
                {ci === 0 && row.flagReason && (
                  <span title={FLAG_REASON[row.flagReason] ? t(FLAG_REASON[row.flagReason]!.detail) : undefined} className="shrink-0 text-[9px]"
                        style={{ color: 'var(--viz-critical)' }}>▲</span>
                )}
                {ci === 0 && row.rtkMetriDelta != null && (
                  <span
                    title={t('rtkChanged', { delta: `${row.rtkMetriDelta > 0 ? '+' : ''}${fmtNumber(row.rtkMetriDelta, 1)}` })}
                    className="shrink-0 rounded px-1 text-[9px] font-semibold"
                    style={{ color: 'var(--viz-series-2)', border: '1px solid var(--viz-series-2)' }}
                  >
                    RTK
                  </span>
                )}
                {ci === 0 && row.presence && row.presence !== 'instance' && (
                  <span title={PRESENCE[row.presence] ? t(PRESENCE[row.presence]!.title) : undefined} className="shrink-0 text-[9px] text-neutral-400">
                    {PRESENCE[row.presence] ? t(PRESENCE[row.presence]!.label) : null}
                  </span>
                )}
              </div>
            )}
          </td>
        )
      })}
    </tr>
  )
})

interface Props {
  data: PageData | undefined
  /** The exact key the page fetched under, so optimistic writes patch the right cache entry. */
  queryKey: readonly unknown[]
  canEdit: boolean
  editorName: string
  showPositions: boolean
  /** Where the formula bar starts — a CSS length, so it can follow the measured nav height. */
  top: string
  /** Phone / tablet: drop the repeated column labels at the foot. On a small screen they cost
   *  a band of rows, and the header is never more than a short scroll away. */
  compact: boolean
  /** Right padding so the last columns clear the floating filter rail on the right. */
  rightInset: number
  /** Absolute index of the first row on this page, so the # gutter counts across pages. */
  rowOffset: number
  /** Current server sort key + direction, so headers can show the active arrow. */
  sort: string
  dir: 'asc' | 'desc'
  /** Toggle sort on a column: same key flips direction, new key starts ascending. */
  onSort: (key: string) => void
  /** Header display: 'full' = the workbook's stacked block, 'short' = one-line short names. */
  headerMode: 'full' | 'short'
  /** Drop position columns that are blank across every loaded row, to reclaim width. */
  hideEmpty: boolean
  /** Grid zoom (1 = 100%). Scales the whole sheet, Excel-style. */
  zoom: number
}

export function EditableGrid({
  data, queryKey, canEdit, editorName, showPositions, top, compact, rightInset, rowOffset,
  sort, dir, onSort, headerMode, hideEmpty, zoom,
}: Props) {
  const t = useT(aufmassMessages)
  const [cursor, setCursor] = useState<{ r: number; c: number }>({ r: 0, c: 0 })
  const [draft, setDraft] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  // The Excel-style formula bar edits independently of the in-cell editor to avoid two inputs
  // fighting for focus: it keeps its own value while focused and commits on Enter / blur.
  const [barValue, setBarValue] = useState('')
  const [barFocused, setBarFocused] = useState(false)

  const gridRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  // true when an edit was OPENED on an existing value (Enter / double-click) → select all so
  // typing replaces it; false when the edit was STARTED by typing a character → caret at end.
  const selectAllOnEdit = useRef(false)
  const qc = useQueryClient()

  const allColumns = useMemo(
    () => buildColumns(data?.columns ?? [], showPositions),
    [data?.columns, showPositions],
  )
  const rows = useMemo(() => data?.rows ?? [], [data?.rows])
  // "Hide empty" only drops POSITION columns with no value on this page — the fixed/editable
  // columns always stay so the row never loses its identity.
  const columns = useMemo(
    () => hideEmpty
      ? allColumns.filter((c) => !c.key.startsWith('pos:') || rows.some((r) => c.get(r).trim() !== ''))
      : allColumns,
    [allColumns, hideEmpty, rows],
  )

  // Refs mirror the latest cursor/rows/columns so the per-row callbacks below can stay STABLE
  // across renders. That is what lets a memoized row skip re-rendering: a click changes `cursor`,
  // which would otherwise re-render all ~1000 rows × 55 cells — the cause of the click lag.
  const cursorRef = useRef(cursor)
  cursorRef.current = cursor
  const rowsRef = useRef(rows)
  rowsRef.current = rows
  const columnsRef = useRef(columns)
  columnsRef.current = columns

  const save = useMutation({
    mutationFn: ({ sessionId, field, value }:
      { sessionId: string; field: string; value: string | null }) =>
      value === null
        ? api.del<void>(`/api/aufmass/overrides/${sessionId}/${field}`)
        : api.put<unknown>(`/api/aufmass/overrides/${sessionId}/${field}`, { value }),

    // Optimistic: a spreadsheet that pauses after every cell is not a spreadsheet. The
    // snapshot is restored wholesale on failure — a half-applied edit is worse than none.
    onMutate: async ({ sessionId, field, value }) => {
      await qc.cancelQueries({ queryKey })
      const previous = qc.getQueryData<PageData>(queryKey)
      qc.setQueryData<PageData>(queryKey, (old) => old && {
        ...old,
        rows: old.rows.map((row) => {
          if (row.sessionId !== sessionId) return row
          const overrides = { ...row.overrides }
          if (value === null) delete overrides[field]
          else {
            overrides[field] = {
              value,
              original: overrides[field]?.original ?? currentOf(row, field),
              editedBy: editorName,
              editedAt: new Date().toISOString(),
            }
          }
          return { ...row, ...applyLocal(field, value), overrides }
        }),
      })
      return { previous }
    },
    onError: (err, _vars, context) => {
      if (context?.previous) qc.setQueryData(queryKey, context.previous)
      setError(err instanceof ApiError
        ? `${err.message}${err.detail ? ` — ${err.detail}` : ''}`
        : t('couldNotSave'))
    },
    // Reconcile with the server, which owns `original` and `editedAt`.
    onSettled: () => { void qc.invalidateQueries({ queryKey }) },
  })

  // Reads cursor/rows/columns from refs so its identity is stable — passed unchanged to every row.
  const commit = useCallback((value: string | null) => {
    const row = rowsRef.current[cursorRef.current.r]
    const col = columnsRef.current[cursorRef.current.c]
    if (!row || !col?.editable || !col.field) return
    // An empty box means "clear this cell" — a real override with a null value. Reverting to
    // the AU value is a separate, explicit action (Ctrl/Cmd+Backspace). Compare against the raw
    // editable value, not the display text, so unchanged placeholder cells are a no-op.
    if (value !== null && value === (col.editValue ?? col.get)(row)) return
    setError(null)
    save.mutate({ sessionId: row.sessionId, field: col.field, value })
  }, [save])

  // Stable per-row handlers (see the refs above). A cell calls these with its own indices.
  const select = useCallback((r: number, c: number) => { setDraft(null); setCursor({ r, c }) }, [])
  const startEdit = useCallback((r: number, c: number) => {
    const col = columnsRef.current[c]
    const row = rowsRef.current[r]
    if (canEdit && col?.editable && col.field && row) {
      selectAllOnEdit.current = true
      setCursor({ r, c })
      setDraft((col.editValue ?? col.get)(row))
    }
  }, [canEdit])

  const move = useCallback((dr: number, dc: number) => {
    setCursor((cur) => ({
      r: Math.min(Math.max(cur.r + dr, 0), Math.max(rows.length - 1, 0)),
      c: Math.min(Math.max(cur.c + dc, 0), Math.max(columns.length - 1, 0)),
    }))
  }, [rows.length, columns.length])

  useEffect(() => {
    if (draft !== null) return
    gridRef.current
      ?.querySelector<HTMLElement>(`[data-cell="${cursor.r}:${cursor.c}"]`)
      ?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }, [cursor, draft])

  // Focus and place the caret ONCE, when an edit begins for a given cell — keyed on the cell,
  // NOT on `draft`. The previous version re-ran on every keystroke and re-selected the text, so
  // each new character replaced the previous one and only the last letter survived.
  const editingKey = draft === null ? null : `${cursor.r}:${cursor.c}`
  useEffect(() => {
    const el = inputRef.current
    if (editingKey === null || !el) return
    el.focus()
    if (selectAllOnEdit.current) el.select()
    else el.setSelectionRange(el.value.length, el.value.length)
  }, [editingKey])

  const onKeyDown = (e: React.KeyboardEvent) => {
    const col = columns[cursor.c]
    const row = rows[cursor.r]

    if (draft !== null) {
      if (e.key === 'Escape') { e.preventDefault(); setDraft(null) }
      else if (e.key === 'Enter') { e.preventDefault(); commit(draft); setDraft(null); move(1, 0) }
      else if (e.key === 'Tab') {
        e.preventDefault(); commit(draft); setDraft(null); move(0, e.shiftKey ? -1 : 1)
      }
      return
    }

    switch (e.key) {
      case 'ArrowUp': e.preventDefault(); move(-1, 0); break
      case 'ArrowDown': e.preventDefault(); move(1, 0); break
      case 'ArrowLeft': e.preventDefault(); move(0, -1); break
      case 'ArrowRight': e.preventDefault(); move(0, 1); break
      case 'Tab': e.preventDefault(); move(0, e.shiftKey ? -1 : 1); break
      case 'Enter':
        e.preventDefault()
        if (canEdit && col?.editable && col.field && row) { selectAllOnEdit.current = true; setDraft((col.editValue ?? col.get)(row)) }
        break
      case 'Backspace':
        if ((e.metaKey || e.ctrlKey) && canEdit && col?.editable && col.field && row?.overrides?.[col.field]) {
          e.preventDefault()
          setError(null)
          save.mutate({ sessionId: row.sessionId, field: col.field, value: null })
        }
        break
      default:
        // A printable key starts the edit and becomes its first character — the behaviour
        // that makes a grid feel like a spreadsheet rather than a form.
        if (canEdit && col?.editable && col.field && row && e.key.length === 1 && !e.metaKey && !e.ctrlKey) {
          e.preventDefault()
          selectAllOnEdit.current = false
          setDraft(e.key)
        }
    }
  }

  // The active cell, for the formula bar. Its raw editable value (or the display value for a
  // read-only cell) is what the bar shows; committing there writes to this same cell.
  const activeRow = rows[cursor.r]
  const activeCol = columns[cursor.c]
  const activeRaw = activeRow && activeCol ? (activeCol.editValue ?? activeCol.get)(activeRow) : ''
  const barEditable = canEdit && !!activeCol?.field
  const stacked = headerMode === 'full'

  return (
    <>
      {error && (
        <p
          role="alert"
          className="fixed bottom-4 left-1/2 z-50 -translate-x-1/2 rounded-2xl bg-white px-4 py-2.5 text-sm shadow-lg dark:bg-neutral-900"
          style={{ boxShadow: '0 8px 30px rgb(0 0 0 / 0.18), inset 0 0 0 1px color-mix(in srgb, var(--viz-critical) 40%, transparent)',
                   color: 'var(--viz-critical)' }}
        >
          {error}
        </p>
      )}

      {/* Excel-style formula bar: shows the active cell (row + column), and edits it from the top.
          Sits above the grid header; the scroll container starts below it. */}
      <div
        className="absolute z-30 flex items-stretch border-b border-neutral-300 bg-neutral-50 text-sm"
        style={{ top, left: 0, right: rightInset, height: FORMULA_BAR_H }}
      >
        <div className="flex w-28 shrink-0 items-center gap-1.5 border-r border-neutral-300 px-2 text-xs md:w-44 md:px-3">
          <span className="tabular shrink-0 text-neutral-400">R{rowOffset + cursor.r + 1}</span>
          <span className="truncate font-medium text-neutral-700" title={activeCol ? tidy(activeCol.label) : ''}>
            {activeCol ? tidy(activeCol.label) : ''}
          </span>
        </div>
        <input
          value={barFocused ? barValue : draft !== null ? draft : activeRaw}
          readOnly={!barEditable}
          placeholder={barEditable ? t('editSelectedCell') : t('readOnlyDerived')}
          onFocus={() => { if (barEditable) { setBarValue(activeRaw); setBarFocused(true) } }}
          onChange={(e) => setBarValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault(); commit(barValue); setBarFocused(false)
              ;(e.target as HTMLInputElement).blur(); move(1, 0)
            } else if (e.key === 'Escape') {
              e.preventDefault(); setBarFocused(false); (e.target as HTMLInputElement).blur()
            }
          }}
          onBlur={() => { if (barFocused) { commit(barValue); setBarFocused(false) } }}
          aria-label={t('formulaBarAria')}
          className="min-w-0 flex-1 bg-white px-3 text-neutral-900 outline-none placeholder:text-neutral-400 read-only:bg-neutral-50 read-only:text-neutral-500"
        />
      </div>

      <div
        ref={gridRef}
        tabIndex={0}
        onKeyDown={onKeyDown}
        role="grid"
        aria-label={t('gridAria')}
        // The scroll container starts below the formula bar (topInset + bar height), so the
        // sticky header sticks to the top of THIS container — clear of both the nav and the bar.
        className="absolute inset-x-0 bottom-0 overflow-auto focus:outline-none"
        style={{ top: `calc(${top} + ${FORMULA_BAR_H}px)` }}
      >
        {/* Full-bleed content, but with a trailing spacer the exact width of the floating filter
            rail. The grid uses the whole viewport width while scrolling; the empty space only
            appears once you scroll to the far-right end, so the last columns can be pushed out
            from under the rail instead of being permanently hidden behind it. */}
        <div className="flex w-max" style={{ zoom }}>
        <table className="w-max border-separate border-spacing-0 text-sm">
          {/* The scroll container starts just below the floating chrome (its `top` is set to
              topInset), so the header only has to stick to the top of that container. No top
              padding, so no padding-vs-sticky double-count, and nothing to slide behind. */}
          <thead className="sticky top-0 z-20">
            <tr>
              <th
                scope="col"
                className="sticky left-0 z-40 w-12 min-w-12 border-b border-r border-neutral-300 bg-neutral-100/95 px-2 py-2 text-center text-xs font-medium text-neutral-400 backdrop-blur"
              >
                #
              </th>
              {columns.map((c, ci) => {
                const active = c.sortKey && c.sortKey === sort
                return (
                  <th
                    key={c.key}
                    scope="col"
                    aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : undefined}
                    style={{ minWidth: c.width, maxWidth: CELL_MAX_W, backgroundColor: excelHeaderColor(c.excel) }}
                    title={c.sortKey ? t('clickToSort') : c.editable ? t('editable') : t('readOnlyDerived')}
                    onClick={c.sortKey ? () => onSort(c.sortKey!) : undefined}
                    className={`border-b border-r border-neutral-300 bg-neutral-100/95 px-2 py-2 text-left text-[11px] font-medium leading-tight backdrop-blur ${
                      stacked ? 'whitespace-pre-line break-words align-bottom' : 'whitespace-nowrap align-middle'
                    } ${ci === 0 ? 'sticky left-12 z-30' : ''} ${
                      c.align === 'right' ? 'text-right' : ''
                    } ${c.sortKey ? 'cursor-pointer select-none hover:brightness-95' : ''} ${
                      active ? 'text-brand-700' : ''
                    }`}
                  >
                    {stacked
                      ? <span title={tidy(c.label)}>{stackLabel(c.label)}</span>
                      : <span className="inline-block max-w-[6rem] truncate align-middle" title={tidy(c.label)}>{shortLabel(c.label)}</span>}
                    {c.editable && <span className="text-brand-600" aria-label={t('editable')}> ✎</span>}
                    {c.sortKey && (
                      <span className={`ml-0.5 ${active ? 'text-brand-700' : 'text-neutral-300'}`} aria-hidden>
                        {active ? (dir === 'asc' ? '▲' : '▼') : '↕'}
                      </span>
                    )}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <GridRow
                key={row.sessionId}
                row={row}
                ri={ri}
                columns={columns}
                rowOffset={rowOffset}
                activeCol={cursor.r === ri ? cursor.c : -1}
                draft={cursor.r === ri ? draft : null}
                onSelect={select}
                onEdit={startEdit}
                setDraft={setDraft}
                commit={commit}
                inputRef={inputRef}
              />
            ))}
          </tbody>

          {/* The column labels repeated at the foot, sticky to the bottom of the scroll area, so
              on a tall page you can read what a column is without scrolling back to the top. */}
          {!compact && <tfoot className="sticky bottom-0 z-20">
            <tr>
              <th
                scope="col"
                className="sticky left-0 z-40 w-12 min-w-12 border-t border-r border-neutral-300 bg-neutral-100/95 px-2 py-2 text-center text-xs font-medium text-neutral-400 backdrop-blur"
              >
                #
              </th>
              {columns.map((c, ci) => (
                <th
                  key={c.key}
                  scope="col"
                  style={{ minWidth: c.width, maxWidth: CELL_MAX_W, backgroundColor: excelHeaderColor(c.excel) }}
                  className={`border-t border-r border-neutral-300 bg-neutral-100/95 px-2 py-2 text-left text-[11px] font-medium leading-tight backdrop-blur ${
                    stacked ? 'whitespace-pre-line break-words align-top' : 'whitespace-nowrap align-middle'
                  } ${ci === 0 ? 'sticky left-12 z-30' : ''} ${c.align === 'right' ? 'text-right' : ''}`}
                >
                  {stacked
                    ? <span title={tidy(c.label)}>{stackLabel(c.label)}</span>
                    : <span className="inline-block max-w-[6rem] truncate align-middle" title={tidy(c.label)}>{shortLabel(c.label)}</span>}
                  {c.editable && <span className="text-brand-600" aria-label={t('editable')}> ✎</span>}
                </th>
              ))}
            </tr>
          </tfoot>}
        </table>
          <div aria-hidden className="shrink-0" style={{ width: rightInset }} />
        </div>
      </div>
    </>
  )
}

/** The row property each editable field writes to, for the optimistic update. */
function applyLocal(field: string, value: string | null): Partial<AufmassRow> {
  switch (field) {
    case 'bauleiter_name': return { bauleiterName: value }
    case 'kolona_leader_name': return { kolonaLeaderName: value }
    case 'city': return { city: value }
    case 'provider_name': return { providerName: value }
    case 'streets': return { streets: value }
    case 'pops': return { pops: value }
    case 'dps': return { dps: value }
    case 'comment': return { comment: value }
    case 'rom': return { rom: value }
    default: return {}
  }
}

/** What the cell shows now — the fallback `original` until the server supplies its own. */
function currentOf(row: AufmassRow, field: string): string | null {
  switch (field) {
    case 'bauleiter_name': return row.bauleiterName
    case 'kolona_leader_name': return row.kolonaLeaderName
    case 'city': return row.city
    case 'provider_name': return row.providerName
    case 'streets': return row.streets
    case 'pops': return row.pops
    case 'dps': return row.dps
    case 'comment': return row.comment
    case 'rom': return row.rom
    default: return null
  }
}
